import { Alert } from 'src/design/atoms/Alert';
import { ColorInput } from 'src/design/atoms/ColorInput';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Switch } from 'src/design/atoms/Switch';
import { TextInput } from 'src/design/atoms/TextInput';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useFormik } from 'formik';
import React from 'react';
import { PayCodeDto, PayCodeRequestDto } from 'src/api/FiretrolApiClient';
import {
  useCreatePayCode,
  useDeletePayCode,
  useUpdatePayCode,
} from 'src/api/queries/organization/usePayCodeMutations';
import { parse422Errors } from 'src/api/utils/errorUtils';
import { getErrorMessage, showSuccessToast } from 'src/utils';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { ModalButton } from 'src/design/molecules/ModalButton';
import * as Yup from 'yup';

const createValidationSchema = () =>
  Yup.object({
    name: Yup.string()
      .required('Name is required')
      .min(1, 'Name must be at least 1 character'),
    rateModifierMultiplier: Yup.number()
      .required('Rate modifier multiplier is required')
      .min(0, 'Rate modifier must be a positive number'),
  });

const initialValues: PayCodeRequestDto = {
  name: '',
  rateModifierMultiplier: 1.0,
  bypassScalePay: false,
  isDirect: true,
  isOvertime: false,
  color: '',
};

export interface IAddPayCodeFormProps {
  selectedPayCode?: PayCodeDto;
  setAddPayCodeOpen: (isOpen: boolean, payCode?: PayCodeDto) => void;
}

export const AddPayCodeForm: React.FC<IAddPayCodeFormProps> = ({
  selectedPayCode,
  setAddPayCodeOpen,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const [validationMessages, setValidationMessages] = React.useState<string[]>(
    [],
  );
  const createPayCode = useCreatePayCode();
  const updatePayCode = useUpdatePayCode();
  const deletePayCodeMutation = useDeletePayCode();

  const closeDialog = () => {
    setAddPayCodeOpen(false);
    formik.resetForm();
    setValidationMessages([]);
  };

  const deletePayCode = async () => {
    if (!selectedPayCode) return;
    setError(null);
    try {
      await deletePayCodeMutation.mutateAsync(selectedPayCode.id);
      showSuccessToast(`${selectedPayCode.name} deleted successfully!`);
      closeDialog();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to delete pay code'));
    }
  };

  const formik = useFormik({
    initialValues: selectedPayCode
      ? {
          id: selectedPayCode.id,
          name: selectedPayCode.name,
          rateModifierMultiplier: selectedPayCode.rateModifierMultiplier,
          bypassScalePay: selectedPayCode.bypassScalePay,
          isDirect: selectedPayCode.isDirect ?? true,
          isOvertime: selectedPayCode.isOvertime ?? false,
          color: selectedPayCode.color ?? '',
        }
      : initialValues,
    validationSchema: createValidationSchema(),
    validateOnMount: true,
    onSubmit: async (values) => {
      setError(null);
      setValidationMessages([]);

      try {
        const payload: PayCodeRequestDto = {
          id: selectedPayCode?.id,
          name: values.name,
          rateModifierMultiplier: values.rateModifierMultiplier,
          bypassScalePay: values.bypassScalePay,
          isDirect: values.isDirect,
          isOvertime: values.isOvertime,
          color: values.color || undefined,
        };

        if (selectedPayCode) {
          await updatePayCode.mutateAsync({
            id: selectedPayCode.id,
            request: payload,
          });
          showSuccessToast(`${values.name} updated successfully!`);
        } else {
          await createPayCode.mutateAsync(payload);
          showSuccessToast(`${values.name} created successfully!`);
        }
        closeDialog();
      } catch (err: unknown) {
        const validationErrors = parse422Errors(err);
        if (validationErrors?.messages?.length > 0) {
          setValidationMessages(validationErrors.messages);
        } else {
          setError(getErrorMessage(err, 'An error occurred'));
        }
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack>
        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}
        {validationMessages.length > 0 && (
          <Alert color="red" title="Validation Errors">
            {validationMessages.map((msg, idx) => (
              <div key={idx}>{msg}</div>
            ))}
          </Alert>
        )}

        <TextInput
          label="Name"
          placeholder="Enter pay code name"
          {...formik.getFieldProps('name')}
          error={formik.touched.name && (formik.errors.name as string)}
          description="The name of the pay code (e.g., Overtime, Double Time)"
        />

        <NumberInput
          label="Rate Modifier Multiplier"
          placeholder="Enter rate modifier"
          {...formik.getFieldProps('rateModifierMultiplier')}
          error={
            formik.touched.rateModifierMultiplier &&
            (formik.errors.rateModifierMultiplier as string)
          }
          onChange={(value) =>
            formik.setFieldValue('rateModifierMultiplier', value)
          }
          min={0}
          step={0.1}
          decimalScale={2}
          description="The multiplier applied to the base rate (e.g., 1.5 for time-and-a-half, 2.0 for double time)"
        />

        {Number(formik.values.rateModifierMultiplier) > 1.0 && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
          >
            On scale-pay jobs, multipliers greater than 1.0 switch the
            calculation to the overtime scale formula:{' '}
            <strong>
              (scale base × multiplier) + (scale fringe − worker benefits)
            </strong>
            . This differs from the standard scale adjustment formula used at
            1.0×.
          </Alert>
        )}

        <Switch
          label="Bypass Scale Pay"
          description="When enabled, this pay code uses the employee's base rate instead of the scale rate on scale jobs."
          checked={formik.values.bypassScalePay}
          onChange={(e) =>
            formik.setFieldValue('bypassScalePay', e.currentTarget.checked)
          }
        />

        <Switch
          label="Direct pay code"
          description="When disabled, this is an indirect pay code (e.g. PTO, Holiday). Hours are grouped separately from jobs in payroll reports and exports."
          checked={formik.values.isDirect}
          onChange={(e) =>
            formik.setFieldValue('isDirect', e.currentTarget.checked)
          }
        />

        <Switch
          label="Overtime"
          description="When enabled, hours logged under this pay code count as overtime rather than regular hours in timesheet totals."
          checked={formik.values.isOvertime}
          onChange={(e) =>
            formik.setFieldValue('isOvertime', e.currentTarget.checked)
          }
        />

        <ColorInput
          label="Color"
          description="Color used to highlight this pay code in timesheets and Excel reports"
          placeholder="Pick a color or leave blank"
          value={formik.values.color ?? ''}
          onChange={(value) => formik.setFieldValue('color', value)}
        />

        <Group justify="space-between" mt="md">
          <Group>
            {selectedPayCode && (
              <InlineConfirmButton
                loading={deletePayCodeMutation.isPending}
                color="red"
                onConfirmClick={deletePayCode}
              >
                Delete Pay Code
              </InlineConfirmButton>
            )}
          </Group>
          <Group>
            <ModalButton onClick={closeDialog} variant="subtle">
              Cancel
            </ModalButton>
            <ModalButton
              type="submit"
              disabled={!formik.isValid}
              loading={formik.isSubmitting}
            >
              {selectedPayCode ? 'Update' : 'Create'}
            </ModalButton>
          </Group>
        </Group>
      </Stack>
    </form>
  );
};
